/**
 * Israeli cities & streets (data.gov.il).
 *
 * data.gov.il no longer sends CORS headers, so the browser cannot call it
 * directly. Everything goes through the `getIsraelCities` / `getIsraelStreets`
 * Cloud Functions, which fetch it server-side and cache the result.
 *
 * A per-session in-memory cache here avoids re-calling the function while the
 * user moves between forms.
 */
import { callFunction } from '@core/firebase/functions.js'

const CALL_OPTIONS = { timeout: 120000 }

let citiesPromise = null
const streetsPromises = new Map()

/**
 * Full list of Israeli cities, sorted in Hebrew.
 * @returns {Promise<Array<{ name: string, code: number|null }>>}
 */
export async function fetchCities() {
  if (!citiesPromise) {
    citiesPromise = callFunction('getIsraelCities', {}, CALL_OPTIONS)
      .then((res) => (Array.isArray(res?.cities) ? res.cities : []))
      .catch((error) => {
        citiesPromise = null // allow a retry on the next call
        throw error
      })
  }
  return citiesPromise
}

/**
 * Street names for a city, sorted in Hebrew.
 * @param {number|string} cityCode - `סמל_ישוב` of the city
 * @returns {Promise<string[]>}
 */
export async function fetchStreets(cityCode) {
  const code = Number(cityCode)
  if (!Number.isInteger(code) || code <= 0) return []

  if (!streetsPromises.has(code)) {
    const promise = callFunction('getIsraelStreets', { cityCode: code }, CALL_OPTIONS)
      .then((res) => (Array.isArray(res?.streets) ? res.streets : []))
      .catch((error) => {
        streetsPromises.delete(code) // allow a retry on the next call
        throw error
      })
    streetsPromises.set(code, promise)
  }
  return streetsPromises.get(code)
}

/** Clears the in-memory caches (mainly for tests / manual refresh). */
export function clearAddressCache() {
  citiesPromise = null
  streetsPromises.clear()
}
